import React, { useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import { UsersIcon, ChevronDownIcon } from '@heroicons/react/24/outline';
import { GameOutput, Player, PlayOutput } from '../generated-api';
import PlayerAvatar from './common/PlayerAvatar';
import {
  MatchResult,
  RivalRecord,
  computeRivalRecords,
  findFavouriteTarget,
  findNemesis,
  recordByGame,
} from '../utils/rivals';

/** Regulars shown before "Show all". */
const REGULARS_SHOWN = 6;
/** Results per game shown in the expanded rivalry (most recent last). */
const RESULTS_PER_GAME = 6;
/** Games listed in the expanded rivalry before "+N more games". */
const GAMES_SHOWN = 8;

interface RivalsSectionProps {
  playerId: string;
  plays: PlayOutput[];
  players: Player[];
  games: GameOutput[];
}

const resultClass: Record<MatchResult, string> = {
  W: 'text-green-700',
  L: 'text-red-700',
  D: 'text-muted',
};

const resultWords: Record<MatchResult, string> = {
  W: 'you won',
  L: 'they won',
  D: 'tied',
};

const RivalsSection: React.FC<RivalsSectionProps> = ({ playerId, plays, players, games }) => {
  const [expandedId, setExpandedId] = useState<string | null>(null);
  const [showAll, setShowAll] = useState(false);

  const records = useMemo(() => computeRivalRecords(playerId, plays), [playerId, plays]);
  const nemesis = useMemo(() => findNemesis(records), [records]);
  const target = useMemo(() => findFavouriteTarget(records), [records]);

  const playerById = useMemo(() => new Map(players.map(p => [p.id, p])), [players]);
  const nameOf = (id: string) => playerById.get(id)?.name ?? 'Unknown player';
  const gameName = (id: string) => games.find(g => g.id === id)?.name ?? 'Unknown game';

  if (records.length === 0) return null;

  // Regulars: people met at least twice; fall back to everyone when nobody qualifies yet.
  const regulars = records.some(r => r.meetings.length >= 2)
    ? records.filter(r => r.meetings.length >= 2)
    : records;
  const shown = showAll ? regulars : regulars.slice(0, REGULARS_SHOWN);

  const renderHighlight = (
    label: string,
    record: RivalRecord | null,
    detail: (r: RivalRecord) => string,
    detailClass: string
  ) => (
    <div className="min-w-0" data-testid={`rivals-${label.toLowerCase().replace(' ', '-')}`}>
      <div className="text-xs uppercase tracking-wide text-muted">{label}</div>
      {record ? (
        <Link
          to={`/players/${record.opponentId}`}
          className="block text-base font-medium text-heading truncate hover:text-primary-700"
        >
          {nameOf(record.opponentId)}
        </Link>
      ) : (
        <div className="text-base text-muted">Nobody yet</div>
      )}
      <div className={`text-xs ${record ? detailClass : 'text-muted'}`}>
        {record ? detail(record) : 'Needs 3+ games together'}
      </div>
    </div>
  );

  const renderExpanded = (record: RivalRecord) => {
    const last = record.meetings[record.meetings.length - 1];
    const byGame = recordByGame(record);
    const hidden = byGame.length - GAMES_SHOWN;
    return (
      <div className="mt-2 mb-1 rounded-lg border border-edge bg-surface-base p-3" data-testid="rival-detail">
        <ul className="space-y-1.5">
          {byGame.slice(0, GAMES_SHOWN).map(game => (
            <li key={game.gameId} className="flex items-center justify-between gap-3 text-sm">
              <span className="text-body truncate min-w-0">{gameName(game.gameId)}</span>
              <span className="font-mono flex-shrink-0 tracking-wider" aria-label={`${game.wins} wins, ${game.losses} losses`}>
                {game.results.slice(-RESULTS_PER_GAME).map((r, i) => (
                  <span key={i} className={`${resultClass[r]} ml-1.5 font-semibold`}>{r}</span>
                ))}
              </span>
            </li>
          ))}
        </ul>
        {hidden > 0 && (
          <p className="mt-1.5 text-xs text-muted">
            +{hidden} more {hidden === 1 ? 'game' : 'games'}
          </p>
        )}
        <Link
          to={`/plays/${last.playId}`}
          className="mt-2 block text-xs text-primary-700 hover:text-primary-800"
        >
          Last met: {gameName(last.gameId)},{' '}
          {new Date(last.playDate).toLocaleDateString(undefined, { day: 'numeric', month: 'short' })} ({resultWords[last.result]})
        </Link>
      </div>
    );
  };

  return (
    <div className="sm:border sm:border-edge sm:rounded-xl sm:p-4" data-testid="rivals-section">
      <h3 className="text-lg font-medium text-heading mb-3 flex items-center">
        <UsersIcon className="h-5 w-5 mr-2" />
        Rivals
      </h3>

      <div className="grid grid-cols-2 gap-3 rounded-xl border border-edge p-3 mb-4">
        {renderHighlight('Nemesis', nemesis, r => `won ${r.losses} of ${r.meetings.length} vs you`, 'text-red-700')}
        {renderHighlight('Favourite target', target, r => `you won ${r.wins} of ${r.meetings.length}`, 'text-green-700')}
      </div>

      <h4 className="text-sm font-medium text-heading mb-1">Head to head</h4>
      <ul>
        {shown.map(record => {
          const decided = record.wins + record.losses;
          const share = decided > 0 ? record.wins / decided : 0.5;
          const expanded = expandedId === record.opponentId;
          const opponent = playerById.get(record.opponentId);
          return (
            <li key={record.opponentId}>
              <button
                type="button"
                data-track="player_rival_toggle"
                aria-expanded={expanded}
                onClick={() => setExpandedId(expanded ? null : record.opponentId)}
                className="w-full min-h-[44px] flex items-center gap-2 py-1.5 text-left hover:bg-surface-hover rounded-md"
              >
                <PlayerAvatar name={nameOf(record.opponentId)} imageUrl={opponent?.imageUrl} size="small" />
                <span className="w-20 sm:w-28 text-sm text-body truncate flex-shrink-0">{nameOf(record.opponentId)}</span>
                <span
                  className="flex-1 h-2.5 rounded-full bg-primary-100 overflow-hidden"
                  role="img"
                  aria-label={`${record.wins} wins, ${record.losses} losses${record.draws ? `, ${record.draws} ties` : ''}`}
                >
                  <span className="block h-full rounded-full bg-primary-600" style={{ width: `${Math.round(share * 100)}%` }} />
                </span>
                <span className="w-14 text-right text-sm font-mono text-heading flex-shrink-0">
                  {record.wins}–{record.losses}
                </span>
                <ChevronDownIcon className={`h-4 w-4 text-muted flex-shrink-0 transition-transform ${expanded ? 'rotate-180' : ''}`} />
              </button>
              {expanded && renderExpanded(record)}
            </li>
          );
        })}
      </ul>
      {regulars.length > REGULARS_SHOWN && (
        <button
          type="button"
          data-track="player_rivals_show_all"
          onClick={() => setShowAll(!showAll)}
          className="w-full min-h-[44px] sm:min-h-0 text-center text-sm text-primary-700 hover:text-primary-800 py-2"
        >
          {showAll ? 'Show less' : `Show all ${regulars.length} rivals`}
        </button>
      )}
      <p className="mt-2 text-xs text-muted">
        Wins–losses = who finished higher in the same play. Teammates don't count. Tap a rival for the per-game record.
      </p>
    </div>
  );
};

export default RivalsSection;
